import React, { useEffect, useState } from 'react';
import withComponentAuth from '../../hoc/withComponentAuth';
import { Box, Button, Typography } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { SmartFilter } from '../../components/common/SmartFilter';
import { headerTechnicianTable } from '../../utils/constants';
import Pagination from '../../components/atoms/Pagination';
import { TechnicianList } from '../../components/ui/list/Technicians';
import { GETTechnicians, BulkDeleteTechnicians } from '../../services/technician';
import { APP_SHELL_HEADER_CONTROL_HEIGHT } from '../../components/layout/layoutConstants';
import { ITechnician } from '../../utils/entities/interface';
import { debounce } from '../../utils/helper';
import { TechnicianFilter } from '../../components/ui/filters/technician';
import {
  TechnicianKycStatus,
  TechnicianPosition,
  TechnicianStatus,
  TechnicianType,
} from '../../utils/entities/type';
import { AddEditTechnicianModal } from '../../components/ui/add/technicianModal';
import { useUrlSearchQuery } from '../../hooks/useUrlSearchQuery';
import { useAdminPermission } from '../../hooks/useAdminPermission';
import { DialogBoxComp } from '../../components/atoms/DialogBox';
import useSnackbar from '../../hooks/useSnackbar';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

export interface TechnicianFilterData {
  type: TechnicianType | any;
  position: TechnicianPosition | any;
  status: TechnicianStatus | any;
  kycStatus: TechnicianKycStatus | any;
}

const TechnicianManagement = () => {
  const { page, rowsPerPage, setPage, setRowsPerPage } = usePaginationParams();
  const [technicians, setTechnicians] = useState<ITechnician[]>([]);
  const [totalTechnician, setTotalTechnician] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [filters, setFilters] = useState<TechnicianFilterData>({
    type: '',
    position: '',
    status: '',
    kycStatus: '',
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const { canDelete } = useAdminPermission();
  const { SnackbarComponent, showSnackbar } = useSnackbar();
  const hasDeletePermission = canDelete('technicians');

  useUrlSearchQuery((query) => {
    setSearchQuery(query);
    setPage(1);
  });

  console.log(technicians, '---');

  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);

  useEffect(() => {
    fetchTechnician(page, rowsPerPage, searchQuery, sort, filters);
  }, [page, rowsPerPage, searchQuery, sort, filters]);

  const fetchTechnician = async (
    page: number,
    limit: number,
    searchQuery?: string,
    sort?: 'ASC' | 'DESC',
    filters?: TechnicianFilterData,
  ) => {
    try {
      setIsLoading(true);
      const response = await GETTechnicians({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
        ...filters,
        kycStatus: 'VERIFIED',
        excludePosition: 'TBA',
        workEligible: true,
      });

      setTechnicians(response.data);
      setTotalTechnician(response.count);
    } catch (error) {
      console.error('Failed to fetch technicians:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const refreshList = () => {
    console.log('REFRESHLIST HAPPED');
    fetchTechnician(page, rowsPerPage, searchQuery, sort, filters);
  };

  const handleFilterRefresh = (filterData: TechnicianFilterData) => {
    console.log('filterData', filterData);
    setFilters({
      ...filterData,
      kycStatus: 'VERIFIED',
      position: filterData.position === 'TBA' ? '' : filterData.position,
    });
    setPage(1); // Reset to the first page when filters change
  };

  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleSortRefresh = () => {
    setSort(sort === 'ASC' ? 'DESC' : 'ASC');
    refreshList();
  };
  const handleAddModal = () => {
    setOpenAddModal(true);
  };
  const handleSaveRefresh = () => {
    refreshList();
  };

  const handleSelectionChange = (ids: string[]) => {
    setSelectedIds(ids);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;

    try {
      setIsDeleting(true);
      const result = await BulkDeleteTechnicians(selectedIds);
      showSnackbar(`${result.deletedCount} technician(s) deleted successfully`, 'success');
      setSelectedIds([]);
      setOpenDeleteModal(false);
      refreshList();
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to delete technicians', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      {SnackbarComponent}
      <SmartFilter
        title="Technicians"
        createFilter={['SEARCH', 'ADD', 'FILTER']}
        addDrawerSize="lg"
        filterdrawerSize="sm"
        filterElement={
          <TechnicianFilter
            onFiltered={handleFilterRefresh}
            filters={filters}
            onResetFilter={() =>
              handleFilterRefresh({
                type: '',
                position: '',
                status: '',
                kycStatus: 'VERIFIED',
              })
            }
          />
        }
        filterHeader={'Filter Technicians'}
        addHeader={'Add Technician'}
        addbuttontitle="Add Technician"
        onFiltered={handleFilterRefresh}
        onSearch={handleSearchRefresh}
        onSort={handleSortRefresh}
        onSaved={refreshList}
        onNavigate={handleAddModal}
        extraActions={
          hasDeletePermission && selectedIds.length > 0 ? (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={() => setOpenDeleteModal(true)}
              sx={{ textTransform: 'none', borderRadius: 2, height: APP_SHELL_HEADER_CONTROL_HEIGHT }}
            >
              Delete ({selectedIds.length})
            </Button>
          ) : null
        }
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
        {openAddModal && (
          <AddEditTechnicianModal
            open={openAddModal}
            onClose={() => setOpenAddModal(false)}
            action="ADNEW"
            onSaved={() => {
              handleSaveRefresh();
              setOpenAddModal(false);
            }}
          />
        )}

        <Box
          ref={scrollRef}
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
          }}
        >
          {technicians.length <= 0 && !isLoading ? (
            <Typography textAlign={'center'} marginTop={'20%'}>
              No Technicians Found
            </Typography>
          ) : (
            <TechnicianList
              headerCells={headerTechnicianTable}
              technicians={technicians}
              isLoading={isLoading}
              onUpdate={refreshList}
              showCheckbox={hasDeletePermission}
              selectedIds={selectedIds}
              onSelectionChange={handleSelectionChange}
            />
          )}
        </Box>

        <Box display={'block'} bottom={0} right={0}>
          <Pagination
            count={totalTechnician}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>

        <DialogBoxComp
          open={openDeleteModal}
          title="Delete Technicians"
          content={`Are you sure you want to delete ${selectedIds.length} technician(s)? This action will disable the selected technician accounts.`}
          handleClose={() => setOpenDeleteModal(false)}
          handleConfirm={handleDeleteSelected}
        />
      </Box>
    </Box>
  );
};

export default withComponentAuth(TechnicianManagement);
